import type { JSX, PropsWithChildren } from 'react';

import { Radio } from '@change-corgi/design-system-legacy/components/forms';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { useSunriseHoldoutExperiment } from '../sunriseHoldoutExperiment/hooks/useSunriseHoldoutExperiment';

import { HighlightText } from './HighlightText';
import { Label } from './Label';

export type Props = PropsWithChildren<{
	highlight?: string;
	label: string;
	message: string;
	onSelect: () => void;
	checked: boolean;
	qaSelectorSelection: string;
	qaSelectorLabel: string;
	qaSelectorMessage: string;
	isSunriseEnabled: boolean;
}>;

// TODO: Improve the complexity of this component once the sunrise experiments are productized

export function Selection({
	qaSelectorSelection,
	qaSelectorLabel,
	qaSelectorMessage,
	label,
	message,
	highlight,
	onSelect,
	checked,
	children,
	...props
}: Props): JSX.Element {
	const { isSunriseEnabled } = useSunriseHoldoutExperiment({
		checked,
		isSunriseEnabled: props.isSunriseEnabled,
	});

	return (
		<Box
			sx={{
				borderRadius: 12,
				p: '2px',
				boxShadow: checked ? 'elevatedSelected' : 'low',
				cursor: 'pointer',
				background: checked ? 'linear-gradient(275deg, #0139BC 2.93%, #4BAEFF 96.39%)' : 'transparent',
				border: checked ? 'none' : '2px solid',
				margin: checked ? '2px' : 0,
				borderColor: 'sunrise.semantic.stroke.weaker',
			}}
			onClick={onSelect}
		>
			{highlight && <HighlightText text={highlight} selected={checked} isSunriseEnabled={isSunriseEnabled} />}
			<Box
				variant="bordered"
				sx={{
					p: 10,
					backgroundColor: checked ? 'sunrise.semantic.fill.accent.primary.weaker' : 'white',
					...(highlight
						? { borderRadius: 'standard', borderTopLeftRadius: 0, borderTopRightRadius: 0 }
						: { borderRadius: 'standard' }),
					...(checked ? { borderWidth: 2 } : {}),
					borderColor: checked ? 'secondary-blue' : 'neutral-grey400',
					borderWidth: 0,
					border: 'none',
					borderRadius: 11, // the parent box has a border radius of 12, so we need to subtract 1px
					// to avoid the border overlap
					px: 24,
					pb: 16,
					pt: highlight ? 12 : 16,
					// TODO: Refactor this component to use buttons instead of radio inputs
					// This is a workaround to hide the radio input while keeping the label accessible.
					'input[type="radio"]': {
						position: 'absolute',
						width: 1,
						height: 1,
						padding: 0,
						margin: -1,
						overflow: 'hidden',
						clip: 'rect(0, 0, 0, 0)',
						whiteSpace: 'nowrap',
						border: 0,
					},
				}}
			>
				<Radio
					data-qa={qaSelectorSelection}
					data-testid="selection-radio"
					checked={checked}
					readOnly
					label={
						<Box
							sx={{
								cursor: 'pointer',
							}}
						>
							<Label qaSelectorLabel={qaSelectorLabel} label={label} />
							<Flex sx={{ paddingTop: '8px', gap: '8', flexDirection: 'column' }}>
								<Text
									data-qa={qaSelectorMessage}
									size="small"
									sx={{
										lineHeight: '20px',
										fontSize: 14,
										fontWeight: 400,
									}}
								>
									{message}
								</Text>
								{children}
							</Flex>
						</Box>
					}
					sx={{
						accentColor: 'sunrise.semantic.fill.accent.primary.strong',
						height: '18px',
						marginTop: '2px',
						width: '18px',
					}}
				/>
			</Box>
		</Box>
	);
}
